/**
 * Spanish locale strings.
 *
 * Keys mirror en.ts exactly. Every key present in en.ts must appear
 * here — the i18n locale-completeness test (lib/i18n/i18n.test.ts)
 * fails on any drift. Translations target Latin American Spanish
 * (the US Hispanic cleaning workforce, ABC's initial market).
 */

const es = {
  auth: {
    signInTo: 'Iniciar sesión en {{name}}',
    emailLabel: 'Correo electrónico',
    passwordLabel: 'Contraseña',
    signingIn: 'Iniciando sesión…',
    signIn: 'Iniciar sesión',
    signOut: 'Cerrar sesión',
    accessDenied: 'Acceso denegado',
    accessDeniedDetail:
      'Tu cuenta no tiene permiso para ver esta página. Contacta a tu supervisor si crees que es un error.',
    goToDashboard: 'Ir al panel',
    biometricSignIn: 'Iniciar sesión con datos biométricos',
    biometricVerifying: 'Verificando…',
    orWithPin: 'o inicia sesión con PIN',
    pinLabel: 'PIN',
    pinHint: 'PIN de 6 dígitos enviado en el correo de bienvenida.',
    forgotPinOpen: '¿Olvidaste tu PIN?',
    forgotPinClose: 'Cancelar',
    forgotPinSending: 'Enviando…',
    forgotPinSubmit: 'Enviarme un nuevo PIN',
    forgotPinSent:
      'Si ese correo está registrado, se envió un nuevo PIN. Revisa tu bandeja de entrada.',
  },

  biometric: {
    promptLabel: 'Inicio biométrico',
    promptTitle: 'Acelera tu próximo inicio de sesión',
    promptBody:
      'Activa Face ID / huella digital para iniciar sesión sin escribir tu PIN.',
    promptEnable: 'Activar',
    promptEnrolling: 'Configurando…',
    promptNotNow: 'Ahora no',
  },

  dashboard: {
    myTasks: 'Mis tareas',
    tapToStart:
      'Toca una tarea para ver su lista de verificación y comenzar a limpiar.',
    loadingTasks: 'Cargando tus tareas…',
    failedToLoadTasks: 'Error al cargar las tareas',
    noTasksAssigned: 'No tienes tareas asignadas en este momento.',
    room: 'Habitación {{number}}',
    clean: 'Limpieza {{type}}',
    started: 'iniciada {{date}}',
    availableJobs: 'Trabajos disponibles',
    tapToTake: '¿Sin trabajo asignado? Toca un trabajo disponible para tomarlo.',
    noAvailableJobs: 'No hay trabajos disponibles ahora. Vuelve a revisar en unos minutos.',
    takeBadge: 'Tocar para tomar',
  },

  task: {
    loadingTask: 'Cargando tarea…',
    failedToLoadTask: 'Error al cargar la tarea',
    taskNotFound: 'Tarea no encontrada.',
    startedLabel: 'Iniciada:',
    notYet: 'aún no',
    completedLabel: 'Completada:',
    blockingIssues: 'Problemas bloqueantes',
    blockingDetail:
      'esta habitación no se puede marcar como lista hasta que se resuelvan:',
    tip: 'Consejo',
    loadingAI: 'Cargando guía de IA…',
    coachingUnavailable:
      'La guía de IA no está disponible temporalmente. La lista de tareas anterior está al día — trabaja a partir de ella y de tu entrenamiento.',
    required: 'Obligatorio ({{done}}/{{total}})',
    optional: 'Opcional',
    noRequiredItems: 'No hay elementos obligatorios para este tipo de habitación.',
    photos: 'Fotos',
    markComplete: 'Marcar hecho',
    markIncomplete: 'Marcar no hecho',
    markNa: 'N/A',
    clearNa: 'Quitar N/A',
    naReasonLabel: 'Motivo (opcional, ≤200 caracteres)',
    naSave: 'Marcar N/A',
    naCancel: 'Cancelar',
    partialScopeLabel: 'Alcance parcial:',
  },

  photoCategory: {
    bathroom_mirror:   'Espejo del baño',
    bathroom_sink:     'Lavabo del baño',
    bathroom_toilet:   'Inodoro',
    bathroom_tub:      'Tina / ducha',
    bedroom_overview:  'Vista general del cuarto',
    bedroom_floor:     'Piso del cuarto',
    bedroom_closet:    'Clóset',
    bedroom_surfaces:  'Superficies del cuarto',
    trash_bags:        'Bolsas de basura',
    appliances:        'Electrodomésticos',
    ceiling_fan:       'Aspas del ventilador de techo',
    baseboards:        'Zócalos',
    oven_interior:     'Interior del horno (puerta abierta)',
    stove_cooktop:     'Superficie de la estufa',
    before:            'Foto antes',
    after:             'Foto después',
    general:           'General',
    damage:            'Daño',
    inventory:         'Inventario',
  },

  startTask: {
    panelLabel:        'Iniciar limpieza',
    prompt:            '¿Listo para empezar? Al iniciar, la tarea pasa a "en progreso" y la habitación entra en limpieza.',
    unassigned:        'Esta tarea aún no tiene un limpiador asignado. Asigne a alguien antes de iniciar.',
    unassignedCleaner: 'Aún no hay limpiador asignado. Pulse Iniciar para reclamar este trabajo y comenzar a limpiar.',
    start:             'Iniciar limpieza',
    starting:          'Iniciando…',
    blockUnassigned:   'Un supervisor debe asignar un limpiador antes de que esta tarea pueda iniciarse.',
    blockNotMine:      'Solo el limpiador asignado puede iniciar esta tarea.',
    blockRole:         'Su rol no puede iniciar esta tarea.',
  },

  assignTask: {
    panelLabel:   'Asignar limpiador a la tarea',
    button:       'Asignar limpiador',
    cleanerLabel: 'Asignar a',
    submitting:   'Asignando…',
    confirm:      'Confirmar asignación',
  },

  counters: {
    legend: 'Se registra al enviar',
    trashBags: 'Bolsas de basura',
    appliances: 'Electrodomésticos limpiados',
  },

  photo: {
    required: 'obligatoria',
    uploaded: '✓ {{count}} cargada(s)',
    preparing: 'Preparando…',
    uploading: 'Subiendo…',
    addAnother: 'Agregar otra',
    addPhoto: 'Agregar foto',
    couldNotProcess: 'No se pudo procesar esta foto. {{message}}',
    runningVisionCheck: 'Analizando con ReadyScan…',
    verdictOk: 'Se ve bien',
    verdictRetake: 'Se recomienda volver a tomarla',
    verdictUnclear: 'La verificación de ReadyScan no está segura',
    verdictUnavailable: 'Verificación de ReadyScan no disponible temporalmente',
    verdictSkipped: 'Verificación de ReadyScan omitida',
  },

  action: {
    submitted: 'Enviado.',
    roomMovedTo: 'La habitación pasó a {{status}}.',
    cannotSubmitYet: 'Aún no se puede enviar',
    submissionBlocked: 'Envío bloqueado',
    submitting: 'Enviando…',
    submitForInspection: 'Enviar tarea para inspección',
    checkingPhotos: 'Verificando cobertura de fotos…',
    coverageMissing: 'Cobertura de fotos incompleta',
    coverageMissingDetail: 'No se detectaron estas fotos requeridas: {{categories}}. Tómalas e intenta enviar de nuevo.',
    coverageUnavailable: 'Verificación de ReadyScan no disponible temporalmente',
    coverageUnavailableBlockerDetail:
      'ReadyScan no puede revisar tus fotos en este momento. Si subiste todas las fotos requeridas, aún puedes enviar — tu supervisor será notificado para verificar manualmente antes de marcar la habitación como lista.',
    coverageUnavailableSubmitAnyway: 'Solicitar aprobación del supervisor y enviar',
    coverageUnavailablePostSubmitDetail:
      'Enviado con marca de aprobación manual — tu supervisor verificará las fotos antes de marcar la habitación como lista.',
    coverageUnavailableCancel: 'Esperar y reintentar',
    coverageTruncated: 'ReadyScan revisó parte de tus fotos',
    coverageTruncatedDetail:
      'Esta tarea tiene más fotos de las que ReadyScan revisa de una vez, así que {{count}} no se revisaron. El resultado de abajo cubre las demás. Si algo aparecía solo en una foto no revisada, tómala de nuevo para que ReadyScan pueda verla.',
    defectsFound: 'Se detectaron problemas de limpieza',
    defectsFoundDetail: 'La inspección de ReadyScan detectó estos problemas que debes volver a limpiar antes de enviar: {{defects}}.',
    defectLabels: {
      baseboard_dust: 'Polvo en los zócalos',
      tub_ring: 'Anillo en la bañera / ducha',
      toilet_ring: 'Anillo en la taza del inodoro',
      mirror_dirty: 'Espejo sucio o con marcas',
      blind_dust: 'Polvo en las persianas',
      stove_food: 'Comida o grasa en la superficie de la estufa',
      oven_food: 'Comida quemada dentro del horno',
      refrigerator_dirty: 'El refrigerador necesita limpieza',
      ceiling_fan_dust: 'Polvo en el ventilador de techo',
      floor_not_vacuumed: 'Piso sin aspirar',
    },
  },

  abandonedBelongings: {
    heading: 'Pertenencias abandonadas',
    openForm: '+ Reportar pertenencias abandonadas',
    descriptionLabel: 'Descripción (1–1000 caracteres)',
    submit: 'Enviar reporte',
    cancel: 'Cancelar',
  },

  abandonedBelongingsQueue: {
    title: 'Pertenencias abandonadas',
    subtitle: 'Reportes de los limpiadores esperando tu decisión.',
    loading: 'Cargando lista…',
    empty: 'No hay reportes de pertenencias abandonadas esperando decisión en este momento.',
    failedToLoad: 'Error al cargar la lista.',
    featureDisabled:
      'El seguimiento de pertenencias abandonadas no está habilitado para esta organización. Contacta a tu administrador para activar este flujo.',
    notesLabel: 'Notas de disposición (opcional, ≤1000 caracteres)',
    acknowledge: 'Reconocer',
    markRemoved: 'Marcar como retirado',
    markBilled: 'Marcar como facturado',
    confirmRemoved: 'Confirmar retirado',
    confirmBilled: 'Confirmar facturado',
    cancel: 'Cancelar',
  },

  feeRequest: {
    heading: 'Solicitudes de tarifa adicional',
    openForm: '+ Solicitar tarifa adicional',
    amountLabel: 'Monto (USD)',
    reasonLabel: 'Motivo (1–1000 caracteres)',
    submit: 'Enviar solicitud',
    cancel: 'Cancelar',
  },

  feeRequests: {
    title: 'Solicitudes de tarifa adicional',
    subtitle: 'Solicitudes de exceso presentadas por los limpiadores esperando tu decisión.',
    loading: 'Cargando lista…',
    empty: 'No hay solicitudes de tarifa esperando decisión en este momento.',
    failedToLoad: 'Error al cargar la lista.',
    featureDisabled:
      'Las solicitudes de tarifa adicional no están habilitadas para esta organización. Contacta a tu administrador para activar este flujo.',
    reasonLabel: 'Motivo (1–1000 caracteres)',
    paidAtNormal: 'Pagado a tarifa normal',
    statusRequested: 'Esperando decisión del propietario',
    statusCompletedWithoutApproval:
      'Tarea ya completada — aún se requiere decisión del propietario',
    approve: 'Aprobar',
    deny: 'Rechazar',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
  },

  inspection: {
    heading: 'Historial de inspección',
    startFirst: 'Iniciar inspección',
    startReinspection: 'Iniciar reinspección',
    decisionLabel: 'Decisión',
    failureReasonLabel: 'Motivo del fallo (1–1000 caracteres)',
    notesLabel: 'Notas internas (opcional, ≤1000 caracteres)',
    submit: 'Enviar decisión',
  },

  anomalies: {
    title: 'Alertas de anomalías',
    subtitle: 'Resultados del análisis entre ciclos — confirma o descarta para resolver.',
    loading: 'Cargando alertas…',
    empty: 'No hay alertas de anomalías abiertas. Buen trabajo.',
    failedToLoad: 'Error al cargar las alertas de anomalías.',
    aiUnavailable: 'Resumen de IA no disponible — revisa las métricas a continuación.',
    detected: 'detectado {{when}}',
    addNotes: 'Agregar notas',
    hideNotes: 'Ocultar notas',
    notesPlaceholder: 'Notas opcionales del revisor',
    confirm: 'Confirmar',
    dismiss: 'Descartar',
    working: 'Procesando…',
    recentReviewed: 'Revisadas recientemente ({{count}})',
  },

  maintenance: {
    title: 'Problemas de mantenimiento',
    subtitle: 'Problemas accionables — bloqueantes y críticos primero.',
    loading: 'Cargando problemas…',
    empty: 'No hay problemas accionables. Buen trabajo.',
    failedToLoad: 'Error al cargar los problemas de mantenimiento.',
    roomLine: 'Habitación {{room}} · {{building}} · abierto {{when}}',
    severityLabel: 'Gravedad',
    categoryLabel: 'Categoría',
    blockingLabel: 'Bloqueante',
    blocking: 'Bloqueante',
    notesLabel: 'Notas de resolución (obligatorio)',
    notesPlaceholder: '¿Qué arreglaste?',
    start: 'Iniciar trabajo',
    starting: 'Iniciando…',
    resolve: 'Resolver',
    resolving: 'Resolviendo…',
    markResolved: 'Marcar como resuelto',
    aiTriageBanner: 'Sugerencia de triaje de IA',
    aiSuggests: 'La IA sugiere',
    aiUnavailable: 'Triaje de IA no disponible — realiza el triaje manualmente.',
    aiMalformed: 'El triaje de IA devolvió un resultado inválido — realiza el triaje manualmente.',
    applyAiSuggestion: 'Aplicar sugerencia',
    applying: 'Aplicando…',
    overrideAiSuggestion: 'Anular',
    dismissAiHint: 'Descartar',
  },

  board: {
    title: 'Tablero de disponibilidad de habitaciones',
    subtitle: 'Vista en vivo de cada habitación, agrupada por estado (urgencia operativa).',
    loading: 'Cargando habitaciones…',
    empty: 'Ninguna habitación coincide con los filtros actuales.',
    failedToLoad: 'Error al cargar el tablero de habitaciones.',
    filterCampus: 'Campus',
    filterBuilding: 'Edificio',
    filterFloor: 'Piso',
    allCampuses: 'Todos los campus',
    allBuildings: 'Todos los edificios',
    allFloors: 'Todos los pisos',
  },

  status: {
    occupied:             'Ocupada',
    move_out_pending:     'Pendiente de mudanza',
    vacant_dirty:         'Vacante (sucia)',
    cleaning_assigned:    'Limpieza asignada',
    cleaning_in_progress: 'Limpieza en curso',
    inspection_pending:   'Inspección pendiente',
    maintenance_required: 'Mantenimiento requerido',
    ready:                'Lista',
    reserved:             'Reservada',
  },

  taskStatus: {
    pending:     'Pendiente',
    in_progress: 'En curso',
    completed:   'Esperando inspección',
    approved:    'Aprobada',
    rejected:    'Rechazada',
  },

  severity: {
    low:      'Baja',
    medium:   'Media',
    high:     'Alta',
    critical: 'Crítica',
  },

  maintenanceIssueStatus: {
    open:        'Abierta',
    assigned:    'Asignada',
    in_progress: 'En curso',
    resolved:    'Resuelta',
    closed:      'Cerrada',
  },

  roomDetail: {
    loading:            'Cargando habitación…',
    failedToLoad:       'Error al cargar la habitación.',
    notFound:           'Habitación no encontrada.',
    title:              'Habitación {{number}}',
    unknownLocation:    'Ubicación desconocida',
    unknownType:        'Tipo desconocido',
    bedCount_one:       '{{count}} cama',
    bedCount_other:     '{{count}} camas',
    statusLabel:        'Estado actual',
    activeTask:         'Tarea de limpieza activa',
    noActiveTask:       'No hay tarea de limpieza activa en esta habitación.',
    taskStatusLabel:    'Estado',
    taskCycleLabel:     'Ciclo',
    taskAssignee:       'Asignada a {{name}}',
    taskUnassigned:     'Sin asignar',
    taskStartedAt:      'Iniciada {{when}}',
    taskSubmittedAt:    'Enviada {{when}}',
    openTask:           'Abrir tarea →',
    maintenanceIssues:  'Problemas de mantenimiento abiertos',
    noIssues:           'No hay problemas de mantenimiento abiertos.',
    blocksReadiness:    'Bloquea listo',
    openedAt:           'Abierta {{when}}',
    history:            'Cambios de estado recientes',
    noHistory:          'Aún no hay cambios de estado registrados.',
    historyTransition:  '{{from}} → {{to}}',
    historyInitial:     'Creada como {{to}}',
  },

  supervisor: {
    pendingInspections: 'Inspecciones pendientes',
    reviewSubmitted: 'Tareas que los limpiadores han enviado, más antiguas primero.',
    loading: 'Cargando lista…',
    empty: 'No hay tareas esperando inspección en este momento.',
    failedToLoad: 'Error al cargar la lista de inspecciones.',
    campusFilterLabel: 'Campus:',
    campusAll: 'Todos los campus',
    room: 'Habitación {{number}}',
    cycleType: 'ciclo {{cycle}}',
    cleanerSubmitted: '{{cleaner}} · enviado {{when}}',
    unknownCleaner: 'Limpiador desconocido',
    unknownTime: 'hora desconocida',
    approve: 'Aprobar',
    approving: 'Aprobando…',
    reject: 'Rechazar',
    rejecting: 'Rechazando…',
    confirmReject: 'Devolver',
    rejectReasonLabel: 'Motivo (obligatorio)',
    rejectReasonPlaceholder: '¿Qué necesita corregir el limpiador?',
    newTaskLink: '+ Nueva tarea',
    governanceLink: 'Correcciones MCP →',
    aiOverrideChip: 'Anulación de ReadyScan — requiere tu revisión',
    myJobs: 'Mis trabajos de limpieza',
    myJobsLabel: 'Trabajos de limpieza asignados a mí',
    myJobsHint: 'Trabajos asignados a ti. Toca para abrir y comenzar la limpieza.',
  },

  createTask: {
    title: 'Nueva tarea de limpieza',
    subtitle:
      'Elige una habitación y un nivel. Los elementos de la lista se cargan automáticamente desde las plantillas de tu contrato.',
    pickRoom: 'Elige una habitación',
    room: 'Habitación',
    roomLabel: 'Habitación',
    cycle: 'Tipo de ciclo',
    tier: 'Nivel de servicio',
    assignee: 'Asignar a (opcional)',
    unassigned: 'Sin asignar — asignar más tarde',
    estimatedMinutes: 'Minutos estimados (opcional)',
    isPartial: 'Esta es una limpieza parcial',
    kitchenExcluded: 'Cocina excluida',
    partialLegend: 'Detalles de limpieza parcial',
    partialScope: 'Descripción del alcance (≤500 caracteres, opcional)',
    submit: 'Crear tarea',
    creating: 'Creando…',
    loading: 'Cargando…',
    cancel: 'Cancelar',
  },

  bulkTask: {
    title: 'Crear tareas en lote',
    subtitle:
      'Crea una tarea por cada habitación que coincida con el alcance siguiente. Hasta 50 habitaciones por lote en el servidor; las selecciones más grandes se procesan automáticamente en varios lotes.',
    campus: 'Campus (filtro opcional)',
    anyCampus: 'Cualquier campus',
    building: 'Edificio',
    anyBuilding: 'Cualquier edificio',
    floor: 'Piso (opcional)',
    anyFloor: 'Cualquier piso',
    pickBuildingFirst: 'Elige primero un edificio',
    cycle: 'Tipo de ciclo',
    tier: 'Nivel de servicio',
    assignee: 'Asignar todas a (opcional)',
    unassigned: 'Sin asignar — asignar más tarde por habitación',
    estimatedMinutes: 'Minutos estimados por tarea (opcional)',
    summaryMatch:
      'Se crearán {{count}} tarea(s) en {{batches}} lote(s) de hasta {{batchSize}} habitaciones cada uno.',
    summaryEmpty: 'Ninguna habitación coincide con el alcance actual. Ajusta los filtros.',
    submit: 'Crear {{count}} tareas',
    creating: 'Creando…',
    loading: 'Cargando…',
    cancel: 'Cancelar',
    resultTitle: 'Resultado',
    created: 'creada(s)',
    failed: 'fallida(s)',
  },

  team: {
    title: 'Equipo',
    subtitle:
      'Todos en tu organización. Invita a limpiadores y supervisores abajo — recibirán un PIN de inicio de sesión por correo.',
    loading: 'Cargando equipo…',
    loadFailed: 'Error al cargar el equipo.',
    empty: 'Aún no hay miembros en el equipo. Invita al primero abajo.',
    roster: 'Equipo actual',
    roleLabel: 'Rol',
    roleCleaner: 'Limpiador',
    roleSupervisor: 'Supervisor',
    promote: 'Hacer supervisor',
    demote: 'Bajar a limpiador',
    updating: 'Guardando…',
    inviteTitle: 'Invitar a miembros del equipo',
    inviteHelp:
      'Pega un correo por línea (o sepáralos con comas). Hasta 50 a la vez. Cada uno recibirá un PIN de 6 dígitos por correo y podrá iniciar sesión de inmediato.',
    emailsLabel: 'Direcciones de correo',
    invite: 'Enviar invitaciones',
    inviting: 'Enviando…',
    invalidEmails: 'Estas direcciones no parecen correos válidos: {{list}}',
    provisionedCount: 'Invitados: {{count}}',
  },

  reports: {
    title: 'Reportes',
    loading: 'Cargando reportes…',
    error: 'No se pudieron cargar los reportes. Intenta recargar.',
    roomReadiness: 'Disponibilidad de habitaciones',
    cleaningOperations: 'Operaciones de limpieza',
    partialUnitApproved: 'Unidades parciales completadas',
    feeRequests: 'Solicitudes de tarifa',
    exceedanceCount: 'Eventos de exceso registrados',
    inspectionsByTier: 'Inspecciones por nivel',
    noInspections: 'Aún no se han registrado inspecciones.',
  },

  leads: {
    title: 'Prospectos',
    subtitle: 'Consultas captadas desde la página de inicio de marketing.',
    loading: 'Cargando prospectos…',
    error: 'No se pudieron cargar los prospectos.',
    empty: 'No hay prospectos en este período.',
    filterVertical: 'Vertical',
    allVerticals: 'Todas las verticales',
    count: '{{count}} mostrados',
    rooms: '{{count}} habitaciones',
    properties: '{{count}} propiedades',
    fitScore: 'Ajuste {{score}}',
    fitUngraded: 'Sin calificar',
  },

  orgSettings: {
    title: 'Configuración de la organización',
    subtitle:
      'Interruptores por flujo de trabajo para tu organización. Los cambios se registran en audit_logs como tenant.feature_flags_changed.',
    on: 'Encendido',
    off: 'Apagado',
    failedToSave: 'Error al guardar la configuración.',
    knownFlagCount:
      'Banderas conocidas: {{count}}. Para agregar otro interruptor de flujo de trabajo derivado del contrato, extiende KNOWN_FLAGS en useTenantFeatures y el arreglo FLAGS de esta página.',
  },

  nav: {
    primary: 'Navegación principal',
    openMenu: 'Abrir menú',
    dashboard: 'Panel',
    rooms: 'Habitaciones',
    newTask: '+ Nueva tarea',
    bulkNewTask: 'Crear en lote',
    team: 'Equipo',
    anomalies: 'Anomalías',
    feeRequests: 'Solicitudes de tarifa',
    abandonedBelongings: 'Pertenencias abandonadas',
    reports: 'Reportes',
    bidTool: 'Cotizador',
    leads: 'Prospectos',
    corrections: 'Correcciones MCP',
    orgSettings: 'Configuración',
    viewingCampus: 'Viendo:',
  },

  common: {
    yes: 'Sí',
    no: 'No',
    cancel: 'Cancelar',
  },

  aiStatus: {
    bannerTitle:  'La verificación con ReadyScan no está disponible en este momento.',
    bannerDetail: 'Los supervisores deberán verificar la cobertura de fotos manualmente antes de marcar las habitaciones como listas.',
  },

  layout: {
    offlineQueued: 'Sin conexión — {{count}} acción{{plural}} en cola.',
    offlineNoQueue:
      'Sin conexión — tus cambios se enviarán cuando te reconectes.',
    reconnecting: 'Reconectando — {{count}} acción{{plural}} pendiente.',
  },

  error: {
    somethingWentWrong: 'Algo salió mal',
    sectionFailed: 'Esta sección no se pudo cargar',
    globalDetail:
      'Ocurrió un error inesperado. Recarga la página para intentarlo de nuevo.',
    routeDetail:
      'Ocurrió un error inesperado en esta sección. Puedes reintentar o recargar la página.',
    reloadPage: 'Recargar página',
    retry: 'Reintentar',
    unexpectedError: 'Ocurrió un error inesperado',
  },

  settings: {
    language: 'Idioma',
    english: 'EN',
    spanish: 'ES',
  },

  serverErrors: {
    incompleteChecklist: 'La lista de verificación no está completa. Por favor termina todos los elementos obligatorios.',
    invalidTransition: 'Esta habitación no puede cambiar a ese estado en este momento.',
    missingPhotos: 'Faltan fotos obligatorias. Por favor súbelas antes de enviar.',
    rateLimited: 'Demasiadas solicitudes. Espera un momento e intenta de nuevo.',
    unauthorized: 'No has iniciado sesión. Por favor inicia sesión e intenta de nuevo.',
    forbidden: 'No tienes permiso para realizar esta acción.',
    notFound: 'No se encontró el elemento solicitado.',
    taskAlreadyCompleted: 'Esta tarea ya ha sido completada.',
    roomNotReady: 'La habitación no está lista para esta transición.',
    blockingIssues: 'Hay problemas de mantenimiento bloqueantes que deben resolverse primero.',
    operationFailed: 'La operación falló. Por favor intenta de nuevo.',
  },
} as const

export default es
